import React, { useEffect, useRef, useState } from 'react';
import { View, StyleSheet, Animated, Easing, Platform } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { WORK_DEMO, WORK_MODE_COLOR } from '@/constants/onboarding';

const STEP_INTERVAL_MS = 1300;
const RESTART_PAUSE_MS = 2600;

/**
 * A looping, simulated Work run: a prompt, then tool steps completing one by
 * one inside a small "computer" frame. Purely illustrative — mirrors the real
 * status labels users will see in Work threads.
 */
export default function WorkDemoCard({ compact = false, demo = WORK_DEMO }) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const prompt = demo?.prompt || WORK_DEMO.prompt;
  const steps = demo?.steps?.length ? demo.steps : WORK_DEMO.steps;
  const [activeIndex, setActiveIndex] = useState(0);
  const pulse = useRef(new Animated.Value(0)).current;
  const progress = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    setActiveIndex(0);
  }, [prompt]);

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 650, easing: Easing.inOut(Easing.ease), useNativeDriver: false }),
        Animated.timing(pulse, { toValue: 0, duration: 650, easing: Easing.inOut(Easing.ease), useNativeDriver: false }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);

  useEffect(() => {
    const isLast = activeIndex >= steps.length - 1;
    const timer = setTimeout(() => {
      setActiveIndex(isLast ? 0 : activeIndex + 1);
    }, isLast ? RESTART_PAUSE_MS : STEP_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [activeIndex, steps.length]);

  useEffect(() => {
    Animated.timing(progress, {
      toValue: (activeIndex + 1) / steps.length,
      duration: activeIndex === 0 ? 0 : 500,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [activeIndex, progress, steps.length]);

  const frameBg = isDark ? 'rgba(255,255,255,0.03)' : '#fff';
  const frameBorder = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)';
  const chromeBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
  const isFinished = activeIndex === steps.length - 1;

  return (
    <View style={[styles.frame, { backgroundColor: frameBg, borderColor: frameBorder, flex: 1 }]}>
      <View style={[styles.chrome, { backgroundColor: chromeBg, borderBottomColor: frameBorder }]}>
        <View style={styles.dots}>
          <View style={[styles.dot, { backgroundColor: '#FF5F57' }]} />
          <View style={[styles.dot, { backgroundColor: '#FEBC2E' }]} />
          <View style={[styles.dot, { backgroundColor: '#28C840' }]} />
        </View>
        <View style={styles.chromeTitle}>
          <Icon name="laptop" size={10} color={colors.text_color_tertiary} />
          <Text size="xxs" color="tertiary" weight="medium">Work's computer</Text>
        </View>
        <View style={[styles.liveBadge, { backgroundColor: isFinished ? 'rgba(16,185,129,0.15)' : `${WORK_MODE_COLOR}22` }]}>
          <Animated.View style={[styles.liveDot, {
            backgroundColor: isFinished ? '#10B981' : WORK_MODE_COLOR,
            opacity: isFinished ? 1 : pulse.interpolate({ inputRange: [0, 1], outputRange: [0.35, 1] }),
          }]} />
          <Text size="xxs" weight="bold" style={{ color: isFinished ? '#10B981' : WORK_MODE_COLOR, fontSize: 9, letterSpacing: 0.5 }}>
            {isFinished ? 'DONE' : 'WORKING'}
          </Text>
        </View>
      </View>

      <View style={[styles.body, compact && styles.bodyCompact]}>
        <View style={[styles.promptBubble, { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }]}>
          <Icon name="user" size={10} color={colors.text_color_tertiary} solid />
          <Text size="xxs" color="secondary" style={styles.promptText} numberOfLines={compact ? 2 : 3}>
            {prompt}
          </Text>
        </View>

        <View style={styles.steps}>
          {steps.map((step, index) => {
            const isDone = index < activeIndex || (isFinished && index === activeIndex);
            const isActive = index === activeIndex && !isFinished;
            const isPending = index > activeIndex;
            const tint = step.done ? '#10B981' : WORK_MODE_COLOR;
            return (
              <View key={step.label} style={[styles.stepRow, isPending && { opacity: 0.32 }]}>
                <View style={[styles.stepIcon, {
                  backgroundColor: isDone ? `${tint}22` : isActive ? `${WORK_MODE_COLOR}18` : 'transparent',
                  borderColor: isDone ? 'transparent' : isActive ? `${WORK_MODE_COLOR}55` : frameBorder,
                }]}>
                  {isDone ? (
                    <Icon name={step.done ? 'circle-check' : 'check'} size={9} color={tint} solid />
                  ) : isActive ? (
                    <Animated.View style={{ opacity: pulse.interpolate({ inputRange: [0, 1], outputRange: [0.4, 1] }) }}>
                      <Icon name={step.icon} size={9} color={WORK_MODE_COLOR} solid />
                    </Animated.View>
                  ) : (
                    <Icon name={step.icon} size={9} color={colors.text_color_tertiary} />
                  )}
                </View>
                <Text
                  size="xxs"
                  weight={isActive || (isDone && step.done) ? 'semibold' : 'medium'}
                  style={[styles.stepLabel, { color: isDone && step.done ? '#10B981' : colors.text_color_primary }]}
                  numberOfLines={1}
                >
                  {step.label}
                </Text>
                {!compact && (isDone || isActive) && (
                  <Text size="xxs" color="tertiary" numberOfLines={1} style={{ fontSize: 10 }}>
                    {step.detail}
                  </Text>
                )}
              </View>
            );
          })}
        </View>

        <View style={[styles.progressTrack, { backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }]}>
          <Animated.View style={[styles.progressFill, {
            backgroundColor: isFinished ? '#10B981' : WORK_MODE_COLOR,
            width: progress.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }),
          }]} />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  frame: {
    width: '100%',
    minHeight: 168,
    borderRadius: 14,
    borderWidth: 1,
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? { boxShadow: '0 10px 30px rgba(0,0,0,0.10)' } : {
      shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.10, shadowRadius: 18,
    }),
  },
  chrome: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderBottomWidth: 1,
  },
  dots: { flexDirection: 'row', gap: 5, width: 50 },
  dot: { width: 8, height: 8, borderRadius: 4 },
  chromeTitle: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  liveBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 999,
    width: 72,
    justifyContent: 'center',
  },
  liveDot: { width: 6, height: 6, borderRadius: 3 },
  body: { flex: 1, padding: 12, gap: 10 },
  bodyCompact: { padding: 10, gap: 8 },
  promptBubble: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 7,
    padding: 9,
    borderRadius: 10,
  },
  promptText: { flex: 1, lineHeight: 15, fontStyle: 'italic' },
  steps: { gap: 6 },
  stepRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  stepIcon: {
    width: 20,
    height: 20,
    borderRadius: 6,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepLabel: { flex: 1 },
  progressTrack: { height: 4, borderRadius: 2, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 2 },
});
